import { useEffect, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getMovieBreadcrumb } from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { getMovieById, getRelatedMovies, siteInfo } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const movie = useMemo(() => getMovieById(Number(id)), [id]);
  const related = useMemo(() => (movie ? getRelatedMovies(movie, 6) : []), [movie]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-400 block mb-4"></i>
            <p className="text-foreground-600 text-lg">影视资源不存在</p>
            <Link to="/" className="mt-4 inline-block px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = getMovieBreadcrumb(movie.title, movie.category);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${movie.title} - 51影院免费在线观看`,
    description: movie.description,
    author: {
      '@type': 'Organization',
      name: '51影院',
    },
    datePublished: `${movie.year}-01-01`,
    dateModified: movie.updateInfo,
    publisher: {
      '@type': 'Organization',
      name: '51影院',
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <article className="px-4 md:px-6 py-6 md:py-10">
              <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
                {/* Poster */}
                <div className="flex-shrink-0 mx-auto lg:mx-0">
                  <div className="w-56 md:w-64 aspect-[2/3] rounded-lg overflow-hidden">
                    <img
                      src={movie.coverImage}
                      alt={movie.title}
                      title={`${movie.title} - 51影院免费在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="mt-4 text-center lg:text-left">
                    <Link
                      to={`/category/${movie.category}`}
                      className="inline-block mt-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line mr-1.5"></i>立即观看
                    </Link>
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                    {movie.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {movie.tags.map(tag => (
                      <span key={tag} className="px-2.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium">
                        {tag}
                      </span>
                    ))}
                    {movie.rating > 0 && (
                      <span className="px-2.5 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-full font-bold">
                        ★ {movie.rating}分
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4 text-sm mb-6">
                    <div className="flex gap-2">
                      <span className="text-foreground-500 whitespace-nowrap">导演：</span>
                      <span className="text-foreground-900">{movie.director}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-foreground-500 whitespace-nowrap">年份：</span>
                      <span className="text-foreground-900">{movie.year}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-foreground-500 whitespace-nowrap">地区：</span>
                      <span className="text-foreground-900">{movie.region}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-foreground-500 whitespace-nowrap">类型：</span>
                      <span className="text-foreground-900">{movie.subCategory}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-foreground-500 whitespace-nowrap">时长：</span>
                      <span className="text-foreground-900">{movie.duration}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-foreground-500 whitespace-nowrap">状态：</span>
                      <span className="text-primary-500 font-medium">{movie.updateInfo}</span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h3 className="font-heading font-semibold text-foreground-900 mb-1 text-sm">主演</h3>
                    <p className="text-foreground-700 text-sm">{movie.actors.join(' · ')}</p>
                  </div>

                  <div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm">剧情简介</h3>
                    <p className="text-foreground-700 text-sm leading-relaxed">{movie.description}</p>
                  </div>
                </div>
              </div>
            </article>

            {/* Related Movies */}
            {related.length > 0 && (
              <section className="px-4 md:px-6 py-8 md:py-10 border-t border-background-200" aria-label="相关推荐">
                <div className="max-w-7xl mx-auto">
                  <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                    {related.map(m => (
                      <MovieCard key={m.id} movie={m} />
                    ))}
                  </div>
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}